import React, { useEffect, useState } from 'react';
import { SimpleProduct, SelectionValue, ProductDetailSelection } from './types';
import { Localizer } from 'services/Localizer';
import classnames from 'classnames';
import { useProductDetailsContext } from './ProductDetailsContext';
import { NoPricingLine } from './ProductPricingLines';

interface ProductSelectionsProps {
	selections?: ProductDetailSelection[] | null;
	simpleProducts?: SimpleProduct[] | null;
	onSimpleProductSelected: (simpleProduct: SimpleProduct) => void;
	onSimpleProductCleared: () => void;
}

const getCheckedLabel = (selection: ProductDetailSelection) => {
	const checkedValue = selection.values.find((value) => value.checked);
	if (!checkedValue) {
		return '';
	}

	return checkedValue.label;
};

/**
 * Handles all selections rendering and logic
 */
export default function ProductSelections(props: ProductSelectionsProps) {
	const [selections, setSelections] = useState<ProductDetailSelection[]>(props.selections || []);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [initialSelectionsValidated, setInitialSelectionsValidated] = useState(false);

	const {
		actor: { setSelectedPricingLine },
	} = useProductDetailsContext();

	useEffect(() => {
		console.debug('Selections Changed');

		// all selections picked?
		// determine child product match
		const checkedSelections = selections.filter((selection) => {
			return (
				selection.values.filter((selectionValue) => {
					return selectionValue.checked;
				}).length > 0
			);
		});

		if (checkedSelections.length === selections.length) {
			// get all values selected by the user. ignore any that aren't selected to easily
			// determine by array length if all selections have been picked
			const checkedSelectionValueIds = checkedSelections
				.map((checkedSelection) => {
					const checkedSelectionValueMatch = checkedSelection.values.find(
						(checkedSelectionValue) => checkedSelectionValue.checked,
					);
					if (!checkedSelectionValueMatch) {
						return '';
					}

					return checkedSelectionValueMatch.value;
				})
				.filter((checkedSelectionValueId) => checkedSelectionValueId);

			if (Array.isArray(props.simpleProducts)) {
				const simpleProductMatch = props.simpleProducts.find((simpleProduct) => {
					return simpleProduct.selections.every((simpleProductSelection) =>
						checkedSelectionValueIds.includes(simpleProductSelection),
					);
				});

				if (isInitialLoad) {
					// Need to update the selections to disable invalid selections, which causes this useEffect to run again
					if (!initialSelectionsValidated) {
						updateValidSelections(selections);
						setInitialSelectionsValidated(true);
					} else {
						// useEffect is running again after validating selections, don't need to do anything here
						setIsInitialLoad(false);
					}
					return;
				}

				if (simpleProductMatch) {
					console.debug('Simple Product:', simpleProductMatch);
					props.onSimpleProductSelected(simpleProductMatch);
				} else {
					props.onSimpleProductCleared();
				}
			}
		} else {
			if (isInitialLoad) {
				// #region HCL
				autoSelectFirstProduct();
				// #endregion
				setInitialSelectionsValidated(true);
				setIsInitialLoad(false);
				return;
			}

			props.onSimpleProductCleared();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selections, props.simpleProducts]);

	if (!Array.isArray(selections)) {
		return null;
	}
	// checks for a matching path only, doesn't check for other factors, such as availability
	const hasMatchingSimpleProduct = (values: string[], simpleProducts: SimpleProduct[]) => {
		return simpleProducts.some((simpleProduct) => {
			return values.every((matchingSelectionValue) => {
				return simpleProduct.selections.includes(matchingSelectionValue);
			});
		});
	};

	// #region HCL
	const autoSelectFirstProduct = () => {
		if (Array.isArray(props.simpleProducts) && props.simpleProducts.length > 0 && selections.length > 0) {
			const firstValidSelectionValue = selections[0].values.find((v) => v.valid);

			const searchParams = new URLSearchParams(document.location.search);
			const firstSimple =
				props.simpleProducts.find((p) => p.sku === searchParams.get('keyword')) ??
				props.simpleProducts.find((p) => p.selections.some((s) => s === firstValidSelectionValue?.value));

			if (firstSimple) {
				console.debug('Auto-selecting product:', firstSimple);

				firstSimple.selections.forEach((selection) => {
					const matchingSelection = selections.find((s) => s.values.some((v) => v.value === selection));
					if (matchingSelection) {
						const matchingValue = matchingSelection.values.find((v) => v.value === selection);
						if (matchingValue) {
							handleSelection(matchingSelection, matchingValue);
						}
					}
				});
			}
		}
	};
	// #endregion

	/**
	 * Update the valid/selectable product selections based on the selection state
	 * @param selectionsToUpdate
	 */
	const updateValidSelections = (selectionsToUpdate: ProductDetailSelection[]) => {
		const newSelections = [...selectionsToUpdate];

		// Store the currently chosen values
		const checkedValues: Map<string, string> = new Map();
		for (const currSelection of newSelections) {
			for (const currSelectionValue of currSelection.values) {
				if (currSelectionValue.checked) {
					checkedValues.set(currSelection.label, currSelectionValue.value);
				}
			}
		}

		for (const newSelection of newSelections) {
			for (const newSelectionValue of newSelection.values) {
				// check if newSelectionValue + selected values from other selections has a matching child
				console.debug('Checking', newSelectionValue.value);

				let hasValidPath: boolean | null = null;

				for (const permutationSelection of newSelections) {
					if (permutationSelection === newSelection) {
						continue;
					}

					for (const permutationSelectionValue of permutationSelection.values) {
						if (!permutationSelectionValue.checked) {
							continue;
						}

						// Check if adding the current selection to the list of selected values will still result in a valid product,
						// ignoring already selected values for the current selection
						const valuesToCheck = new Map(checkedValues).set(newSelection.label, newSelectionValue.value);
						const currentList = Array.from(valuesToCheck.values());
						const hasMatch = hasMatchingSimpleProduct(currentList, props.simpleProducts || []);
						hasValidPath = hasMatch;

						if (hasMatch) {
							break;
						}
					}
				}

				if (hasValidPath !== null) {
					console.debug('Valid Path:', hasValidPath);
					newSelectionValue.valid = hasValidPath;
				} else {
					newSelectionValue.valid = true;
				}
			}
		}

		// updates state for all selections
		setSelections(newSelections);
	};

	const handleSelection = (selection: ProductDetailSelection, selectionValue: SelectionValue) => {
		if (selectionValue.checked) {
			return;
		}

		setSelectedPricingLine(NoPricingLine);
		const newSelections = [...selections];
		const clickedSelectionValueIsValid = selectionValue.valid;

		console.debug('Selecting', selectionValue.label);
		console.debug('Clicked Valid Selection:', clickedSelectionValueIsValid);

		// 1. mark clicked selection as checked.
		// 2. if clicked selection is not valid, uncheck all other selections
		// 3. update all other selections for validity
		for (const newSelection of newSelections) {
			for (const newSelectionValue of newSelection.values) {
				if (selection === newSelection) {
					// matching selection of selection value clicked
					if (newSelectionValue === selectionValue) {
						// matching selection value clicked
						newSelectionValue.checked = true;
					} else {
						newSelectionValue.checked = false;
					}
				} else {
					// if we clicked on a possibly invalid path, uncheck all other selections
					if (!clickedSelectionValueIsValid) {
						newSelectionValue.checked = false;
					}
				}
			}
		}

		console.debug('Simple Products:', props.simpleProducts);

		// check validity of all permutations
		updateValidSelections(newSelections);
	};

	return (
		<>
			<fieldset className="roc-pdp-selections">
				<legend className="roc-visually-hidden">{Localizer('Selections')}</legend>
				{selections.map((selection, selectionIndex) => {
					const selectionLabel = getCheckedLabel(selection);
					return (
						<div key={selectionIndex} className="roc-pdp-selections__item">
							<p className="roc-pdp-selections__item-title">
								<strong>{selection.label}</strong>{' '}
								{selectionLabel && ' - ' + getCheckedLabel(selection)}
							</p>
							<div className="roc-pdp-selections__item-content">
								{selection.values.map((selectionValue, index) => {
									// #region HCL - swatch handling
									const useSwatches = selection.values.some((v) => v.swatchUrl);
									const selectionClassNames = useSwatches
										? 'roc-pdp-selections__item-label roc-pdp-selections__color roc-pdp-selections__color--' +
										  selectionValue.label.toLowerCase()
										: 'roc-pdp-selections__item-label';
									// #endregion
									return (
										<label
											htmlFor={
												'selections-' + selectionIndex.toString() + '-' + selectionValue.label
											}
											key={index}
											// #region HCL - handling swatches

											style={
												useSwatches
													? {
															backgroundImage: selectionValue.swatchUrl
																? `url(${selectionValue.swatchUrl})`
																: 'none',
													  }
													: {}
											}
											className={classnames(selectionClassNames, {
												'roc-pdp-selections__item-label--active': selectionValue.checked,
												'roc-pdp-selections__item-label--invalid': !selectionValue.valid,
											})}
											// #endregion
										>
											{/* #region HCL - swatches */}
											{useSwatches ? '' : selectionValue.label}
											{/* #endregion HCL */}

											<input
												className="roc-pdp-selections__item-input roc-visually-hidden"
												key={index}
												type="radio"
												id={
													'selections-' +
													selectionIndex.toString() +
													'-' +
													selectionValue.label
												}
												data-testid={
													'selections-' +
													selectionIndex.toString() +
													'-' +
													selectionValue.label
												}
												value={selectionValue.value}
												name={'selections-' + selectionIndex.toString()}
												checked={selectionValue.checked}
												onChange={() => {
													const match = selection.values.find(
														(value) => value.value === selectionValue.value,
													);

													if (match) {
														handleSelection(selection, match);
													}
												}}
											/>
										</label>
									);
								})}
							</div>
						</div>
					);
				})}
			</fieldset>
		</>
	);
}
